import { AccordionContent } from "@/components/ui/library/accordion";
import { ChildDocUpload } from "@/module/components/components/ChildDocUpload";
import { ChildDocumentCard } from "@/module/components/components";
import { useAcademyChildModuleData } from "@/module/academy/helpers/useAcademyChildModuleData";
import useProgramVariants from "@/features/academy/api/useProgramVariants";
import usePipeline from "@/features/stage/api/usePipeline";
import { filterSequentialSubStatuses } from "@/features/leads/utils/sequentialSubStatus";
import { useStatusRbac } from "@/rbac/useStatusRbac";
import { useChildMutations } from "@/features/leads/api/useChildMutations";
import { refetchChilds } from "@/features/leads/api/useChilds";
import { refetchCurrentChild } from "@/features/leads/api/useCurrentChild";
import { refetchCurrentLead } from "@/features/leads/api/useCurrentLead";
import { useCreatePayment } from "@/features/leads/api/usePaymentMutations";
import { useDocumentMutaion } from "@/hooks/api/useDocmentMutation";
import BankNameInput from "@/features/leads/components/view-lead/components/Ongoing/components/BankNameInput";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/library/select";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/library/avatar";
import { Button } from "@/components/ui/button";
import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import {
  ChevronRight,
  Calendar,
  CircleDot,
  Plus,
  IndianRupee,
  Eye,
  Upload,
  Loader2,
  X,
} from "lucide-react";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import { usePermissions } from "@/rbac";
import { getAvatarUrl } from "@/config/endpoints";
import { toast } from "sonner";
import { getRelativeTime } from "@/utils/date";
import { useSmartRoute } from "@/module/hook/useSmartRoute";
import { useAppDispatch } from "@/stores/hook";
import {
  setLeadCurrentTab,
  setPaymentsDealIdToOpen,
} from "@/features/leads/stores/leadFunctionalSlice";

const fmt = (v: number) =>
  v.toLocaleString("en-IN", { maximumFractionDigits: 0 });

function nowDatetimeLocal() {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const PAYMENT_METHODS = [
  "UPI",
  "Bank Transfer",
  "Cash",
  "Card",
  "Cheque",
] as const;

const STATUS_BADGE: Record<
  string,
  { label: string; bg: string; text: string }
> = {
  advance: { label: "Advance Paid", bg: "#e0f2fe", text: "#0369a1" },
  partial: { label: "Partial", bg: "#fef3c7", text: "#92400e" },
  full: { label: "Fully Paid", bg: "#dcfce7", text: "#166534" },
  pending: { label: "Pending", bg: "#fee2e2", text: "#991b1b" },
};

export default function AcademyChildContent({
  child,
  onDirtyChange,
  onRegisterUpdate,
}: {
  child: any;
  onDirtyChange?: (dirty: boolean) => void;
  onRegisterUpdate?: (fn: (() => void) | null) => void;
}) {
  const goToProgramTab = useSmartRoute();
  const dispatch = useAppDispatch();

  if (!child) return null;

  const { variantId, variantName } = useAcademyChildModuleData(child);
  const { variants } = useProgramVariants();
  const { pipeline } = usePipeline();
  const { updateStage } = useChildMutations();
  const { lead } = useActiveDataMutation();
  const createPayment = useCreatePayment();
  const { uploadFile } = useDocumentMutaion();
  const { canStage } = usePermissions();

  const variant = useMemo(
    () => variants?.find((v: any) => v.id === variantId),
    [variants, variantId]
  );

  const courseFees = variant?.courseFees ?? 0;

  const dealValue = child.totalDealValue || courseFees;
  const netAmount = child.netAmount || dealValue;
  const totalPaid = child.totalPaid || 0;
  const pendingAmount = child.pendingAmount ?? netAmount - totalPaid;
  const discountAmount = child.discountAmount || 0;
  const paymentStatus = child.paymentStatus || "pending";

  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("UPI");
  const [payBankName, setPayBankName] = useState("");
  const [payPaidAt, setPayPaidAt] = useState(nowDatetimeLocal);
  const [payNote, setPayNote] = useState("");
  const [payReceiptDocId, setPayReceiptDocId] = useState<number | null>(null);
  const [payReceiptFileName, setPayReceiptFileName] = useState("");
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const parsedPayAmount = parseFloat(payAmount) || 0;
  const isPayAmountValid =
    parsedPayAmount > 0 && parsedPayAmount <= pendingAmount;

  const resetPaymentForm = () => {
    setPayAmount("");
    setPayMethod("UPI");
    setPayBankName("");
    setPayPaidAt(nowDatetimeLocal());
    setPayNote("");
    setPayReceiptDocId(null);
    setPayReceiptFileName("");
  };

  const handleRecordPayment = () => {
    if (!isPayAmountValid) return;
    createPayment.mutate(
      {
        childId: child.id,
        amount: parsedPayAmount,
        method: payMethod,
        paidAt: payPaidAt ? new Date(payPaidAt).toISOString() : undefined,
        txnId: payBankName || undefined,
        note: payNote || undefined,
        receiptDocId: payReceiptDocId ?? undefined,
      },
      {
        onSuccess: () => {
          resetPaymentForm();
          setShowPaymentForm(false);
        },
      }
    );
  };

  const handleReceiptUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingReceipt(true);
    setPayReceiptFileName(file.name);
    uploadFile.mutate(
      {
        file,
        attachedTo: "child",
        attachedScope: String(child.id),
        category: "payment",
        name: file.name,
      },
      {
        onSuccess: (data: any) => {
          const docId = data?.data?.id ?? data?.document?.id ?? data?.id;
          if (docId) setPayReceiptDocId(docId);
          setUploadingReceipt(false);
        },
        onError: () => {
          setPayReceiptFileName("");
          setUploadingReceipt(false);
        },
      }
    );
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const serverStatusId = child?.status?.id
    ? String(child.status.id)
    : undefined;
  const serverSubStatusId = child?.subStatus?.id
    ? String(child.subStatus.id)
    : undefined;
  const serverStageId = child?.stage?.id ? String(child.stage.id) : undefined;

  const [statusId, setStatusId] = useState<string | undefined>(serverStatusId);
  const [subStatusId, setSubStatusId] = useState<string | undefined>(
    serverSubStatusId
  );

  useEffect(() => {
    setStatusId(serverStatusId);
    setSubStatusId(serverSubStatusId);
  }, [serverStatusId, serverSubStatusId]);

  const isDirty =
    statusId !== serverStatusId || subStatusId !== serverSubStatusId;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const currentStage = useMemo(
    () => pipeline?.find((s: any) => String(s.id) === serverStageId),
    [pipeline, serverStageId]
  );

  // Role-denied statuses (wizard "Status" block) are hidden from the
  // picker; the deal's server/draft status is pinned so a deal already
  // sitting in a denied status still displays it.
  const { filterRoleAllowedStatuses, isStatusDeniedByRole } = useStatusRbac();
  const statuses = useMemo(
    () =>
      filterRoleAllowedStatuses(
        currentStage?.id,
        currentStage?.statuses ?? currentStage?.statusList ?? [],
        [serverStatusId, statusId]
      ),
    [currentStage, filterRoleAllowedStatuses, serverStatusId, statusId]
  );

  const selectedStatus = useMemo(
    () => statuses.find((st: any) => String(st.id) === statusId),
    [statuses, statusId]
  );

  const subStatuses = useMemo(
    () => selectedStatus?.subStatuses ?? [],
    [selectedStatus]
  );

  // "Sequence wise" statuses offer one sub-status step at a time (see
  // filterSequentialSubStatuses); the full list still backs the save fallback.
  const subStatusOptions = useMemo(
    () =>
      filterSequentialSubStatuses(subStatuses, selectedStatus?.isSequential, {
        entering: statusId !== serverStatusId,
        currentSubStatusId: serverSubStatusId,
        pinnedIds: [serverSubStatusId, subStatusId],
      }),
    [subStatuses, selectedStatus, statusId, serverStatusId, serverSubStatusId, subStatusId]
  );

  const nextStage = useMemo(() => {
    if (!pipeline?.length || !currentStage) return null;
    const sorted = [...pipeline].sort(
      (a: any, b: any) => (a.order ?? 0) - (b.order ?? 0)
    );
    const idx = sorted.findIndex((s: any) => s.id === currentStage.id);
    return idx >= 0 && idx < sorted.length - 1 ? sorted[idx + 1] : null;
  }, [pipeline, currentStage]);

  const handleUpdateStatus = useCallback(async () => {
    if (!child?.id || !statusId || !serverStageId) return;
    const resolvedSubStatusId = subStatusId || subStatuses[0]?.id;
    if (!resolvedSubStatusId) {
      toast.error("Please select a valid sub-status.");
      return;
    }
    const data = {
      stageId: Number(serverStageId),
      statusId: Number(statusId),
      subStatusId: Number(resolvedSubStatusId),
    };
    try {
      await updateStage.mutateAsync({ id: child.id, data });
      await Promise.all([
        refetchChilds(),
        refetchCurrentChild(),
        refetchCurrentLead(),
      ]);
    } catch {
      // handled by mutation
    }
  }, [
    child?.id,
    statusId,
    subStatusId,
    serverStageId,
    subStatuses,
    updateStage,
  ]);

  useEffect(() => {
    onRegisterUpdate?.(isDirty ? handleUpdateStatus : null);
  }, [isDirty, handleUpdateStatus, onRegisterUpdate]);

  const handleMoveStage = async () => {
    if (!child?.id || !nextStage) return;
    const nextStatuses = nextStage.statuses ?? nextStage.statusList ?? [];
    // Skip role-denied statuses when auto-picking the landing status; fall
    // back to the raw first so the move surfaces the server's 403 instead
    // of silently doing nothing.
    const firstStatus =
      nextStatuses.find(
        (st: any) => !isStatusDeniedByRole(nextStage.id, st.id)
      ) ?? nextStatuses[0];
    const firstSub = firstStatus?.subStatuses?.[0];
    if (!firstStatus || !firstSub) {
      toast.error("Next stage has no valid status/sub-status configured.");
      return;
    }
    const data = {
      stageId: Number(nextStage.id),
      statusId: Number(firstStatus.id),
      subStatusId: Number(firstSub.id),
    };
    try {
      await updateStage.mutateAsync({ id: child.id, data });
      toast.success(`Moved to ${nextStage.name}`);
      await Promise.all([
        refetchChilds(),
        refetchCurrentChild(),
        refetchCurrentLead(),
      ]);
    } catch {
      // handled by mutation
    }
  };

  const childUsers = (child?.childUsers ?? [])
    .map((u: any) => u?.user)
    .filter(Boolean);
  const displayUser =
    childUsers[0] ??
    lead?.leadUsers?.find((u: any) => u.isLeadOwner)?.user ??
    lead?.leadUsers?.[0]?.user ??
    null;

  const createdTime = child?.createdAt
    ? getRelativeTime(child.createdAt)
    : null;
  const stageName = currentStage?.name || child?.stage?.name || "General";
  const statusBadge = STATUS_BADGE[paymentStatus] || STATUS_BADGE.pending;

  return (
    <AccordionContent className="pt-2 pb-1">
      <div className="flex items-center gap-1 mb-1.5">
        <Select
          value={statusId}
          onValueChange={(val) => {
            setStatusId(val);
            const newStatus = statuses.find((st: any) => String(st.id) === val);
            const firstSub = newStatus?.subStatuses?.[0];
            setSubStatusId(firstSub ? String(firstSub.id) : undefined);
          }}
        >
          <SelectTrigger className="h-[26px] rounded-[6px] border-[#d4d8d6] bg-white text-[11px] px-2 w-auto min-w-0 flex-1">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {statuses.map((st: any) => (
              <SelectItem key={st.id} value={String(st.id)}>
                {st.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {subStatuses.length > 0 && (
          <Select value={subStatusId} onValueChange={setSubStatusId}>
            <SelectTrigger className="h-[26px] rounded-[6px] border-[#d4d8d6] bg-white text-[11px] px-2 w-auto min-w-0 flex-1">
              <SelectValue placeholder="Sub-status" />
            </SelectTrigger>
            <SelectContent>
              {subStatusOptions.map((ss: any) => (
                <SelectItem key={ss.id} value={String(ss.id)}>
                  {ss.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="grid grid-cols-4 mb-1.5 rounded-[8px] overflow-hidden border border-[#e6e8e7] bg-[#fafbfb]">
        <MetricCell label="Amount" value={`₹${fmt(dealValue)}`} />
        <MetricCell
          label="Discount"
          value={discountAmount > 0 ? `-₹${fmt(discountAmount)}` : "—"}
        />
        <MetricCell label="Received" value={`₹${fmt(totalPaid)}`} accent />
        <MetricCell label="Pending" value={`₹${fmt(pendingAmount)}`} warn />
      </div>

      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
        {createdTime && (
          <span className="inline-flex items-center gap-1 text-[10px] text-[#9ba3a0]">
            <Calendar className="size-2.5" />
            {createdTime}
          </span>
        )}
        {variantName && (
          <span className="text-[10px] text-[#697571] font-medium">
            {variantName}
          </span>
        )}
        <span
          className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-[1px] rounded-full"
          style={{ backgroundColor: statusBadge.bg, color: statusBadge.text }}
        >
          <CircleDot className="size-2.5" />
          {statusBadge.label}
        </span>
      </div>

      {showPaymentForm && (
        <div className="mb-1.5 p-2 rounded-[8px] border border-[#c1e1d5] bg-[#fafbfa] flex flex-col gap-1.5">
          <p className="text-[9px] font-semibold text-[#8a9590] uppercase tracking-[0.4px]">
            Record Payment
          </p>
          <div className="grid grid-cols-3 gap-1.5">
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-[#9ba3a0]">
                ₹
              </span>
              <input
                type="number"
                placeholder={`Max ${fmt(pendingAmount)}`}
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                max={pendingAmount}
                min={1}
                className={`h-6 w-full pl-5 pr-2 text-[11px] rounded-[5px] border bg-white tabular-nums ${
                  payAmount && !isPayAmountValid
                    ? "border-red-300"
                    : "border-[#e2e4e3]"
                }`}
              />
            </div>
            <select
              value={payMethod}
              onChange={(e) => setPayMethod(e.target.value)}
              className="h-6 px-2 text-[11px] rounded-[5px] border border-[#e2e4e3] bg-white"
            >
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <BankNameInput
              value={payBankName}
              onChange={setPayBankName}
              placeholder="Bank"
            />
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <input
              type="datetime-local"
              value={payPaidAt}
              onChange={(e) => setPayPaidAt(e.target.value)}
              className="h-6 px-2 text-[11px] rounded-[5px] border border-[#e2e4e3] bg-white"
            />
            <input
              type="text"
              placeholder="Note (optional)"
              value={payNote}
              onChange={(e) => setPayNote(e.target.value)}
              className="h-6 px-2 text-[11px] rounded-[5px] border border-[#e2e4e3] bg-white"
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf"
                onChange={handleReceiptUpload}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingReceipt}
                className="flex items-center gap-1 h-5 px-2 text-[9px] font-medium rounded-[4px] border border-dashed border-[#c5c9c7] text-[#697571] hover:bg-[#f7f9f8] disabled:opacity-50"
              >
                {uploadingReceipt ? (
                  <Loader2 className="size-2.5 animate-spin" />
                ) : (
                  <Upload className="size-2.5" />
                )}
                {payReceiptFileName || "Receipt"}
              </button>
              {payReceiptDocId && (
                <button
                  onClick={() => {
                    setPayReceiptDocId(null);
                    setPayReceiptFileName("");
                  }}
                  className="size-4 flex items-center justify-center rounded-full hover:bg-[#fee2e2] text-[#9ba3a0]"
                >
                  <X className="size-2.5" />
                </button>
              )}
            </div>
            <div className="flex gap-1.5">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowPaymentForm(false);
                  resetPaymentForm();
                }}
                className="h-5 text-[9px] px-2 rounded-[4px]"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={!isPayAmountValid || createPayment.isPending}
                onClick={handleRecordPayment}
                className="h-5 text-[9px] px-2 rounded-[4px]"
              >
                {createPayment.isPending ? (
                  <Loader2 className="size-2.5 animate-spin" />
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {(!child.isFroze || child?.documents?.length > 0) && (
        <div className="flex flex-col gap-1.5 mb-1.5">
          {!child.isFroze && (
            <ChildDocUpload
              childId={child.id}
              stageName={stageName}
              childName={child.name}
            />
          )}
          {child?.documents?.length > 0 && (
            <div className="flex gap-1.5 items-start flex-wrap">
              {child.documents.map((doc: any) => (
                <ChildDocumentCard key={doc.id} document={doc} />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between pt-1.5 border-t border-[#f0f2f1]">
        {displayUser ? (
          <div className="flex gap-1 items-center">
            <Avatar className="size-[18px] shrink-0">
              <AvatarImage src={getAvatarUrl(displayUser?.name || "NA")} />
              <AvatarFallback className="text-[7px] font-medium bg-[#351A75] text-white">
                {(displayUser?.name || "NA").slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span className="text-[10px] text-[#697571]">
              {displayUser?.name}
            </span>
          </div>
        ) : (
          <span className="text-[10px] text-[#9ba3a0]">Unassigned</span>
        )}

        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              dispatch(setPaymentsDealIdToOpen(String(child.id)));
              dispatch(setLeadCurrentTab("Payments"));
            }}
            className="h-7 px-2.5 rounded-[8px] text-[11px] font-semibold border-[#c5d5f0] bg-[#f0f5ff] hover:bg-[#e0ecff] text-[#4a7cc9] gap-1"
          >
            <Eye className="size-3" />
            Payments
          </Button>

          {pendingAmount > 0 && !child.isFroze && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setShowPaymentForm(!showPaymentForm);
              }}
              className="h-7 px-2.5 rounded-[8px] text-[11px] font-semibold border-[#267355] bg-[#E9FCF5] hover:bg-[#d0f5e8] text-[#267355] gap-1"
            >
              <IndianRupee className="size-3" />
              Record
            </Button>
          )}

          {!child.isFroze && (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                goToProgramTab();
              }}
              className="h-7 px-3 rounded-[8px] text-[11px] font-semibold border-[#267355] bg-[#E9FCF5] hover:bg-[#d0f5e8] text-[#267355] gap-1"
            >
              <Plus className="size-3" />
              New Deal
            </Button>
          )}
          {nextStage &&
            !child.isFroze &&
            canStage(Number(currentStage?.id), "stage_management", "move") && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMoveStage();
                }}
                disabled={updateStage.isPending}
                className="h-7 px-3 rounded-[8px] text-[11px] font-semibold border-[#add7c7] bg-[#f0f7f5] hover:bg-[#e5f3ed] text-[#1e302a] gap-1"
              >
                {nextStage.name}
                <ChevronRight className="size-3" />
              </Button>
            )}
        </div>
      </div>
    </AccordionContent>
  );
}

function MetricCell({
  label,
  value,
  accent,
  warn,
}: {
  label: string;
  value: string;
  accent?: boolean;
  warn?: boolean;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-1 border-r border-[#e6e8e7] last:border-r-0">
      <span className="text-[8px] font-semibold text-[#9ba3a0] uppercase tracking-[0.4px] leading-none">
        {label}
      </span>
      <span
        className={`text-[11px] font-semibold tabular-nums leading-tight mt-0.5 ${
          accent ? "text-[#267355]" : warn ? "text-[#c4630a]" : "text-[#051912]"
        }`}
      >
        {value}
      </span>
    </div>
  );
}
